import React, { useEffect, useState } from "react";
import { Col, Row, Form, Button, Modal, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import {
  FaBoxOpen,
  FaDownload,
  FaFileExcel,
  FaRupeeSign,
  FaUtensils,
} from "react-icons/fa";
import {
  useCreateFoodItemMutation,
  useUploadPhotoMutation,
} from "../slices/FoodItemSilce";
import FieldWarning from "../components/FieldWarning";
import { useNavigate } from "react-router-dom";
import { setCredentials } from "../slices/authSlice";
import { makeFirstCharCapital } from "../utils/commonUtils";
import * as XLSX from "xlsx";
import {
  cardStyle,
  labelStyle,
  Req,
  SectionCard,
  YesNoRadios,
} from "../components/OutletFormKit";

const CreateFoodItems = ({ outlet, creatable }) => {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // When rendered standalone (route /createFoodItems) there is no `outlet`
  // prop - fall back to the admin's currently selected outlet.
  const selectedOutlet =
    outlet ??
    userInfo?.outletId?.find((oi) => oi._id === userInfo?.selectedOutletId) ??
    (Array.isArray(userInfo?.outletId) ? userInfo.outletId[0] : undefined);

  const [itemCode, setItemCode] = useState("");
  const [uniqueItemCode, setUniqueItemCode] = useState("");
  const [itemCategory, setItemCategory] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");

  const [localName, setLocalName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [onlineAvailableThreshold, setOnlineAvailableThreshold] = useState("");
  const [itemName, setItemName] = useState("");
  const [currentlySelling, setCurrentlySelling] = useState(true);
  const [discountpercent, setDiscountPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [itemGst, setItemGst] = useState("");
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileInvalidError, setFileInvalidError] = useState(false);

  const [itemCodePresent, setItemCodePresent] = useState(false);
  const [itemCategoryPresent, setItemCategoryPresent] = useState(false);
  const [originalPricePresent, setOriginalPricePresent] = useState(false);
  const [offerPricePresent, setOfferPricePresent] = useState(false);
  const [quantityPresent, setQuantityPresent] = useState(false);
  const [itemNamePresent, setItemNamePresent] = useState(false);
  const [currentlySellingPresent, setCurrentlySellingPresent] = useState(false);
  const [localNamePresent, setLocalNamePresent] = useState(false);
  const [isVeg, setIsVeg] = useState(true);
  const [isAvailableInMoreSizes, setIsAvailableInMoreSizes] = useState(false);
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newCategoryPresent, setNewCategoryPresent] = useState(false);
  const [itemGstPresent, setItemGstPresent] = useState(false);
  const [sizeDetails, setSizeDetails] = useState([]);
  const [availableSizes, setAvailableSizes] = useState(0);
  const sizesData = userInfo?.tempSizes || [];
  const [description, setDescription] = useState("");
  const [makingCost, setMakingCost] = useState("");
  const [showExcelErrorModal, setShowExcelErrorModal] = useState(false);

  const [excelValidationErrors, setExcelValidationErrors] = useState([]);

  let categoryOptions = [
    ...(selectedOutlet?.foodCategories || []),
    "Add New Category",
  ];
  const dispatch = useDispatch();

  const [addFoods, { isLoading }] = useCreateFoodItemMutation();

  const [uploadPhoto, { isLoading: isPhotoUploading }] =
    useUploadPhotoMutation();

  useEffect(() => {
    if (itemCode?.trim().length > 0) {
      setItemCodePresent(false);
    }

    if (itemCategory?.trim().length > 0) {
      setItemCategoryPresent(false);
    }
    if (!isAvailableInMoreSizes)
      if (originalPrice !== "" && !isNaN(Number(originalPrice))) {
        setOriginalPricePresent(false);
      }

    if (itemName?.trim().length > 0) {
      setItemNamePresent(false);
    }
    if (quantity !== "" && !isNaN(Number(quantity))) {
      setQuantityPresent(false);
    }
    if (itemCategory === "Add New Category") {
      if (newCategory?.length > 2) {
        setNewCategoryPresent(false);
      }
    }

    if (itemGst && !isNaN(Number(itemGst))) {
      setItemGstPresent(false);
    }
  }, [
    itemCode,
    itemCategory,
    originalPrice,
    quantity,
    itemName,
    itemGst,
    currentlySelling,
    newCategory,
  ]);

  const verifyInputFields = () => {
    let fieldValidationSuccess = true;

    if (!itemCode || itemCode.trim().length < 1) {
      setItemCodePresent(true);
      fieldValidationSuccess = false;
    }

    if (
      !itemCategory ||
      itemCategory.trim().length < 1 ||
      itemCategory === ""
    ) {
      setItemCategoryPresent(true);
      fieldValidationSuccess = false;
    }

    if (!isAvailableInMoreSizes)
      if (!originalPrice?.trim() || isNaN(Number(originalPrice))) {
        setOriginalPricePresent(true);
        fieldValidationSuccess = false;
      }

    if (!quantity?.trim() || isNaN(Number(quantity))) {
      setQuantityPresent(true);
      fieldValidationSuccess = false;
    }

    if (!itemName || itemName.trim().length < 1) {
      setItemNamePresent(true);
      fieldValidationSuccess = false;
    }

    if (currentlySelling === null) {
      setCurrentlySellingPresent(true);
      fieldValidationSuccess = false;
    }

    if (itemCategory === "Add New Category") {
      if (newCategory?.length < 2) {
        setNewCategoryPresent(true);
        fieldValidationSuccess = false;
      }
    }

    if (!itemGst || isNaN(Number(itemGst))) {
      setItemGstPresent(true);
      fieldValidationSuccess = false;
    }

    if (!fieldValidationSuccess) {
      toast.error(
        "Some of the fields have invalid inputs. Please provide valid details"
      );
    }

    return fieldValidationSuccess;
  };

  const handleCancel = () => {
    if (creatable) {
      creatable(false);
    } else {
      navigate("/allFoods");
    }
  };

  const submitHandler = async () => {
    if (verifyInputFields()) {
      try {
        if (isAvailableInMoreSizes) {
          for (let i = 0; i < sizesData.length; i++) {
            const sd = sizesData[i];

            if (
              !sd.sizeName ||
              !sd.itemPrice ||
              !sd.consumptionQty ||
              !sd.makingCost ||
              Number(sd.itemPrice) === 0 ||
              Number(sd.consumptionQty) === 0 ||
              Number(sd.makingCost) === 0
            ) {
              toast.error("Please fill all size details and click Add Sizes");
              return;
            }
          }
        }
        const resData = await addFoods({
          outletId: selectedOutlet?._id,
          itemCode: itemCode,
          uniqueItemCode: uniqueItemCode.trim(),
          isVeg: isVeg,
          itemCategory:
            itemCategory === "Add New Category"
              ? `${newCategory}_newCategory`
              : `${itemCategory}`,
          itemQuantity: quantity,
          onlineAvailableThreshold:
            onlineAvailableThreshold !== ""
              ? Number(onlineAvailableThreshold)
              : 0,
          itemName: makeFirstCharCapital(itemName),
          localName: localName,
          addedBy: userInfo?.adminName,
          currentlySelling: currentlySelling,
          gstTax: itemGst,
          availableSizes:
            isAvailableInMoreSizes && availableSizes > 0
              ? sizesData.map((item) => ({
                  sizeName: item.sizeName,
                  itemPrice: Number(item.itemPrice),
                  discountPercent: Number(item.discountPercent),
                  discountAmount: Number(item.discountAmount),
                  consumptionQty: Number(item.consumptionQty),
                  sizeDescription: item.sizeDescription,
                  makingCost: Number(item.makingCost),
                }))
              : [
                  {
                    sizeName: "Single Size",
                    itemPrice: originalPrice,
                    discountPercent: discountpercent,
                    discountAmount: discountAmount,
                    makingCost: makingCost !== "" ? Number(makingCost) : 0,
                  },
                ],
          itemDescription: description,
          makingCost: makingCost,
          token: userInfo.token,
        }).unwrap();
        if (resData && file) {
          let formData = new FormData();
          formData.append("image", file);
          formData.append("imageKey", `${resData._id}`);
          await uploadPhoto({
            token: userInfo.token,
            payload: formData,
          }).unwrap();
        }
        if (resData) {
          toast.success("Item Added Successfully");
          dispatch(
            setCredentials({
              ...userInfo,
              tempSizes: [],
            })
          );
          setItemCode("");
          setUniqueItemCode("");
          setItemCategory("");
          setOriginalPrice("");
          setLocalName("");
          setQuantity("");
          setOnlineAvailableThreshold("");
          setItemName("");
          setCurrentlySelling(true);
          setDiscountPercent("");
          setDiscountAmount("");
          setItemGst("");
          setFile(null);
          if (creatable) {
            creatable();
          } else {
            navigate("/allFoods");
          }
        }
      } catch (e) {
        toast.error(e?.data?.error || e?.error);
      }
    }
  };

  const compressImage = async (selectedImage) => {
    const file = selectedImage;
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 280,
          useWebWorker: true,
          initialQuality: 1,
        };

        console.log("Un-Compressed file:", file);
        const compressedFile = await imageCompression(file, options);
        console.log("first Compressed file:", compressedFile);
        const secondOptions = {
          maxSizeMB: 1, // maximum size in MB
          useWebWorker: true, // use web worker for performance
          initialQuality: 0.9, // initial quality setting
        };

        // Second compression step
        const secondCompressedFile = await imageCompression(
          compressedFile,
          secondOptions
        );
        console.log("Second Compressed file:", secondCompressedFile);
        setFile(secondCompressedFile);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e?.target?.files[0];
    const dotIndex = selectedFile?.name?.lastIndexOf(".");
    const fileExtension = selectedFile?.name?.slice(dotIndex + 1).toLowerCase();
    let errorPresent = false;
    if (
      !(
        fileExtension === "jpeg" ||
        fileExtension === "jpg" ||
        fileExtension === "png" ||
        fileExtension === "tiff" ||
        fileExtension === "svg" ||
        fileExtension === "webp" ||
        fileExtension === "avif" ||
        fileExtension === "jfif"
      )
    ) {
      errorPresent = true;
      setFileInvalidError(true);
      setFile(null);
    } else if (selectedFile?.size / 1000 > 5120) {
      setFileSizeError(true);
      setFile(null);
    } else if (!errorPresent && selectedFile?.size / 1000 <= 5120) {
      setFileSizeError(false);
      setFileInvalidError(false);
      await compressImage(selectedFile);
    }
  };

  const sampleExcelData = [
    {
      item_Code: 101,
      item_Name: "Masala Dosa",
      local_Name: "Masala Dosa",
      item_Category: "Tiffen",
      new_Category: "",
      is_Veg: "Yes",
      currently_Selling: "Yes",
      item_Quantity: 100,
      item_Gst: 5,
      is_Available_In_More_Sizes: "No",

      size1: "",
      size2: "",
      size3: "",

      original_Price: 80,
      discount_Percent: 10,
      discount_Amount: 0,
      making_Cost: 40,
      description: "",
    },
    {
      item_Code: 102,
      item_Name: "Grill Chicken",
      local_Name: "Grill Chicken",
      item_Category: "Non Veg",
      new_Category: "",
      is_Veg: "No",
      currently_Selling: "Yes",
      item_Quantity: 50,
      item_Gst: 5,
      is_Available_In_More_Sizes: "Yes",

      size1: "Small|120|5|0|0.25|250gm|80",
      size2: "Medium|180|10|0|0.5|500gm|120",
      size3: "Large|250|15|0|1|1kg|180",

      original_Price: "",
      discount_Percent: "",
      discount_Amount: "",
      making_Cost: "",
      description: "",
    },
  ];

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      toast.error("Please upload only Excel files");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const binaryStr = evt.target.result;

        const workbook = XLSX.read(binaryStr, {
          type: "binary",
        });

        const sheetName = workbook.SheetNames[0];

        const worksheet = workbook.Sheets[sheetName];

        const rawExcelData = XLSX.utils.sheet_to_json(worksheet);

        const excelData = rawExcelData.filter((row) => {
          return Object.keys(row).some((key) => {
            return (
              key !== "__rowNum__" &&
              row[key] !== undefined &&
              row[key] !== null &&
              String(row[key]).trim() !== ""
            );
          });
        });
        console.log("Excel Data :", excelData);

        const existingItemCodes =
          userInfo?.AllFoods?.map((item) => Number(item.itemCode)) || [];

        const duplicateItemCodes = excelData
          .map((row) => Number(row.item_Code))
          .filter((code) => !isNaN(code) && existingItemCodes.includes(code));

        if (duplicateItemCodes.length > 0) {
          setExcelValidationErrors([
            {
              rowNo: "-",
              errors: [
                `Item code already exists - ${[
                  ...new Set(duplicateItemCodes),
                ].join(", ")}. Please provide different item code.`,
              ],
            },
          ]);

          setShowExcelErrorModal(true);

          return;
        }

        const excelItemCodes = excelData.map((row) => Number(row.item_Code));

        const duplicateCodesInExcel = excelItemCodes.filter(
          (code, index) => excelItemCodes.indexOf(code) !== index
        );

        if (duplicateCodesInExcel.length > 0) {
          toast.error(
            `Duplicate item codes found in Excel - ${[
              ...new Set(duplicateCodesInExcel),
            ].join(", ")}`
          );

          return;
        }
        const validationErrors = [];

        for (let index = 0; index < excelData.length; index++) {
          const row = excelData[index];

          const rowErrors = [];

          if (
            row.item_Code === undefined ||
            row.item_Code === null ||
            row.item_Code === ""
          ) {
            rowErrors.push("item_Code is required");
          } else if (isNaN(Number(row.item_Code))) {
            rowErrors.push("item_Code must be numeric");
          }

          if (
            row.item_Name === undefined ||
            row.item_Name === null ||
            String(row.item_Name).trim() === ""
          ) {
            rowErrors.push("item_Name is required");
          } else {
            const itemName = String(row.item_Name).trim();

            if (!/^[A-Za-z ]+$/.test(itemName)) {
              rowErrors.push("item_Name must contain only alphabets");
            }
          }

          if (
            (!row.item_Category || String(row.item_Category).trim() === "") &&
            (!row.new_Category || String(row.new_Category).trim() === "")
          ) {
            rowErrors.push("Either item_Category or new_Category is required");
          }

          if (
            row.is_Veg === undefined ||
            row.is_Veg === null ||
            String(row.is_Veg).trim() === ""
          ) {
            rowErrors.push("is_Veg is required");
          } else {
            const vegValue = String(row.is_Veg).toLowerCase();

            if (
              vegValue !== "yes" &&
              vegValue !== "no" &&
              vegValue !== "true" &&
              vegValue !== "false"
            ) {
              rowErrors.push("is_Veg must be Yes/No");
            }
          }

          if (
            row.currently_Selling === undefined ||
            row.currently_Selling === null ||
            String(row.currently_Selling).trim() === ""
          ) {
            rowErrors.push("currently_Selling is required");
          } else {
            const sellingValue = String(row.currently_Selling).toLowerCase();

            if (
              sellingValue !== "yes" &&
              sellingValue !== "no" &&
              sellingValue !== "true" &&
              sellingValue !== "false"
            ) {
              rowErrors.push("currently_Selling must be Yes/No");
            }
          }

          if (
            row.item_Quantity === undefined ||
            row.item_Quantity === null ||
            row.item_Quantity === ""
          ) {
            rowErrors.push("item_Quantity is required");
          } else if (isNaN(Number(row.item_Quantity))) {
            rowErrors.push("item_Quantity must be numeric");
          }

          if (
            row.item_Gst === undefined ||
            row.item_Gst === null ||
            row.item_Gst === ""
          ) {
            rowErrors.push("item_Gst is required");
          } else if (isNaN(Number(row.item_Gst))) {
            rowErrors.push("item_Gst must be numeric");
          } else if (Number(row.item_Gst) < 0) {
            rowErrors.push("item_Gst cannot be negative");
          } else if (Number(row.item_Gst) > 100) {
            rowErrors.push("item_Gst cannot be greater than 100");
          }
          if (
            row.is_Available_In_More_Sizes === undefined ||
            row.is_Available_In_More_Sizes === null ||
            String(row.is_Available_In_More_Sizes).trim() === ""
          ) {
            rowErrors.push("is_Available_In_More_Sizes is required");
          }

          const isAvailableInMoreSizes =
            String(row.is_Available_In_More_Sizes).trim().toLowerCase() ===
              "yes" ||
            String(row.is_Available_In_More_Sizes).trim().toLowerCase() ===
              "true";
          if (!isAvailableInMoreSizes) {
            if (
              row.original_Price === undefined ||
              row.original_Price === null ||
              row.original_Price === ""
            ) {
              rowErrors.push("original_Price is required");
            } else if (isNaN(Number(row.original_Price))) {
              rowErrors.push("original_Price must be numeric");
            } else if (Number(row.original_Price) < 0) {
              rowErrors.push("original_Price cannot be negative");
            }
          }

          if (!isAvailableInMoreSizes) {
            if (
              row.making_Cost === undefined ||
              row.making_Cost === null ||
              row.making_Cost === ""
            ) {
              rowErrors.push("making_Cost is required");
            } else if (isNaN(Number(row.making_Cost))) {
              rowErrors.push("making_Cost must be numeric");
            }
          }

          if (isAvailableInMoreSizes) {
            const sizeColumns = ["size1", "size2", "size3"];

            const filledSizes = sizeColumns.filter(
              (col) => row[col] && String(row[col]).trim() !== ""
            );

            if (filledSizes.length === 0) {
              rowErrors.push(
                "At least one size column is required for multi-size items"
              );
            }

            filledSizes.forEach((col) => {
              const sizeValue = String(row[col]);

              const splitted = sizeValue.split("|");

              if (splitted.length !== 7) {
                rowErrors.push(`${col} format is invalid`);
                return;
              }

              const [
                sizeName,
                itemPrice,
                discountPercent,
                discountAmount,
                consumptionQty,
                sizeDescription,
                makingCost,
              ] = splitted;

              if (!sizeName?.trim()) {
                rowErrors.push(`${col} sizeName is required`);
              }

              if (isNaN(Number(itemPrice))) {
                rowErrors.push(`${col} itemPrice must be numeric`);
              }

              if (isNaN(Number(discountPercent))) {
                rowErrors.push(`${col} discountPercent must be numeric`);
              }

              if (isNaN(Number(discountAmount))) {
                rowErrors.push(`${col} discountAmount must be numeric`);
              }

              if (isNaN(Number(consumptionQty))) {
                rowErrors.push(`${col} consumptionQty must be numeric`);
              }

              if (!sizeDescription?.trim()) {
                rowErrors.push(`${col} sizeDescription is required`);
              }

              if (isNaN(Number(makingCost))) {
                rowErrors.push(`${col} makingCost must be numeric`);
              }
            });
          }

          if (rowErrors.length > 0) {
            validationErrors.push({
              rowNo: index + 2,
              errors: rowErrors,
            });
          }
        }

        if (validationErrors.length > 0) {
          console.log("Validation Errors :", validationErrors);

          setExcelValidationErrors(validationErrors);
          setShowExcelErrorModal(true);

          toast.error("Excel validation failed");

          return;
        }

        console.log("Validated Excel Data :", excelData);

        const finalPayload = [];

        for (const row of excelData) {
          const isAvailableInMoreSizes =
            String(row.is_Available_In_More_Sizes).trim().toLowerCase() ===
              "yes" ||
            String(row.is_Available_In_More_Sizes).toLowerCase() === "true";

          let sizesData = [];

          if (isAvailableInMoreSizes) {
            const sizeColumns = ["size1", "size2", "size3"];

            sizesData = sizeColumns
              .filter((col) => row[col] && String(row[col]).trim() !== "")
              .map((col) => {
                const [
                  sizeName,
                  itemPrice,
                  discountPercent,
                  discountAmount,
                  consumptionQty,
                  sizeDescription,
                  makingCost,
                ] = String(row[col]).split("|");

                return {
                  sizeName: sizeName || "",

                  itemPrice: Number(itemPrice),

                  discountPercent: Number(discountPercent),

                  discountAmount: Number(discountAmount),

                  consumptionQty: Number(consumptionQty),

                  sizeDescription: sizeDescription || "",

                  makingCost: Number(makingCost),
                };
              });
          }

          const payload = {
            outletId: selectedOutlet?._id,

            itemCode: Number(row.item_Code),

            isVeg:
              String(row.is_Veg).trim().toLowerCase() === "yes" ||
              String(row.is_Veg).toLowerCase() === "true",

            itemCategory:
              row.item_Category && String(row.item_Category).trim() !== ""
                ? row.item_Category === "Add New Category"
                  ? `${row.new_Category}_newCategory`
                  : `${row.item_Category}`
                : `${row.new_Category}_newCategory`,
            itemQuantity: Number(row.item_Quantity),

            itemName: makeFirstCharCapital(row.item_Name),

            localName: row.local_Name || "",

            addedBy: userInfo?.adminName,

            currentlySelling:
              String(row.currently_Selling).trim().toLowerCase() === "yes" ||
              String(row.currently_Selling).trim().toLowerCase() === "true",

            gstTax: Number(row.item_Gst),

            availableSizes:
              isAvailableInMoreSizes && sizesData.length > 0
                ? sizesData.map((item) => ({
                    sizeName: item.sizeName,

                    itemPrice: Number(item.itemPrice),

                    discountPercent: Number(item.discountPercent),

                    discountAmount: Number(item.discountAmount),

                    consumptionQty: Number(item.consumptionQty),

                    sizeDescription: item.sizeDescription,

                    makingCost: Number(item.makingCost),
                  }))
                : [
                    {
                      sizeName: "Single Size",

                      itemPrice: Number(row.original_Price),

                      discountPercent: Number(row.discount_Percent),

                      discountAmount: Number(row.discount_Amount),

                      makingCost:
                        row.making_Cost !== "" ? Number(row.making_Cost) : 0,
                    },
                  ],

            itemDescription: row.description || "",

            makingCost: row.making_Cost !== "" ? Number(row.making_Cost) : 0,

            token: userInfo.token,
          };

          finalPayload.push(payload);
        }

        console.log("Final Payload :", finalPayload);

        toast.success("Excel validated successfully");
      } catch (error) {
        console.log("Excel Upload Error :", error);
        toast.error("Error uploading excel file");
      } finally {
        e.target.value = null;
      }
    };

    reader.readAsBinaryString(file);
  };

  const exportToExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(sampleExcelData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "FoodItemsSample");

      XLSX.writeFile(workbook, "FoodItemsSample.xlsx");
    } catch (error) {
      toast.error("Error downloading sample file");
    }
  };

  useEffect(() => {
    if (isAvailableInMoreSizes) setAvailableSizes(2);
  }, [isAvailableInMoreSizes]);

  useEffect(() => {
    if (Number(availableSizes) < 1) return;

    const count = Number(availableSizes);
    let updated = [...(userInfo?.tempSizes || [])];

    if (updated.length < count) {
      for (let i = updated.length; i < count; i++) {
        updated.push({
          sizeName: "",
          itemPrice: "",
          discountPercent: "",
          discountAmount: "",
          consumptionQty: "",
          sizeDescription: "",
          makingCost: "",
        });
      }
    } else {
      updated = updated.slice(0, count);
    }

    dispatch(
      setCredentials({
        ...userInfo,
        tempSizes: updated,
      })
    );
  }, [availableSizes]);

  const handleSizeDetails = ({ idx, fieldName, value }) => {
    const updated = [...sizesData];

    updated[idx] = {
      ...updated[idx],
      [fieldName]: value,
    };

    dispatch(
      setCredentials({
        ...userInfo,
        tempSizes: updated,
      })
    );
  };

  const handleSubmit = () => {
    // Convert numeric fields to numbers

    const formattedSizes = sizesData.map((item) => ({
      sizeName: item.sizeName,
      itemPrice: Number(item.itemPrice),
      discountPercent: Number(item.discountPercent),
      discountAmount: Number(item.discountAmount),
      consumptionQty: Number(item.consumptionQty),
      sizeDescription: item.sizeDescription,
      makingCost: Number(item.makingCost),
    }));

    for (let i = 0; i < formattedSizes.length; i++) {
      const sd = formattedSizes[i];
      if (
        sd.sizeName === null ||
        sd.sizeName === undefined ||
        sd.sizeName?.length < 1 ||
        Number(sd.itemPrice) === null ||
        Number(sd.itemPrice) === undefined ||
        Number(sd.itemPrice) === 0 ||
        Number(sd.consumptionQty) === null ||
        Number(sd.consumptionQty) === undefined ||
        Number(sd.consumptionQty) === 0 ||
        Number(sd.makingCost) === null ||
        Number(sd.makingCost) === undefined ||
        Number(sd.makingCost) === 0
      ) {
        toast.error("Size name, Price and Reduce quantity are mandatory");
        return;
      }
    }
    setSizeDetails(formattedSizes);
    toast.success("Different sizes created");
  };

  const sizeFieldStyle = {
    border: "1px solid #e3ecf3",
    borderRadius: "10px",
    padding: "0.75rem",
    marginBottom: "0.75rem",
    backgroundColor: "#f9fcfe",
  };

  return (
    <div
      style={{
        padding: "0.5rem 0.6rem",
        maxWidth: "960px",
        margin: "0 auto",
      }}
    >
      <Modal
        show={showExcelErrorModal}
        onHide={() => setShowExcelErrorModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Excel Validation Errors</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Table bordered striped>
            <thead>
              <tr>
                <th>Row No</th>
                <th>Errors</th>
              </tr>
            </thead>

            <tbody>
              {excelValidationErrors.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.rowNo}</td>

                  <td>
                    <ul style={{ marginBottom: 0 }}>
                      {item.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowExcelErrorModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Page header */}
      <div
        style={{ ...cardStyle, padding: "1rem 1.1rem", marginBottom: "0.9rem" }}
      >
        <Row className="align-items-center" style={{ rowGap: "0.6rem" }}>
          <Col xs={12} md={5}>
            <h5 className="mb-1">
              <strong style={{ color: "#1d5f91" }}>Create Food Item</strong>
            </h5>
            <small className="text-muted">
              Add a new item to your outlet's menu
            </small>
          </Col>
          <Col
            xs={12}
            md={7}
            className="d-flex flex-wrap justify-content-md-end"
            style={{ gap: "0.5rem" }}
          >
            <Button
              as="label"
              variant="outline-primary"
              size="sm"
              style={{ marginBottom: 0, cursor: "pointer" }}
            >
              <FaFileExcel style={{ marginBottom: "2px" }} /> Create Items Using
              Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleExcelUpload}
              />
            </Button>
            <Button variant="outline-success" size="sm" onClick={exportToExcel}>
              <FaDownload style={{ marginBottom: "2px" }} /> Download Sample
              File
            </Button>
          </Col>
        </Row>
      </div>

      <Form>
        {/* Item details */}
        <SectionCard
          icon={<FaUtensils size={15} />}
          title="Item Details"
          subtitle="Name, code and category of the food item"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Item Name
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter item name"
                value={itemName}
                onChange={(e) => {
                  // Letters of any language (with their vowel signs),
                  // digits, spaces and common punctuation, so names can be
                  // typed or pasted in any script
                  const val = e.target.value.replace(
                    /[^\p{L}\p{M}\p{N} .,&()'/+-]/gu,
                    ""
                  );
                  setItemName(val);
                }}
              />
              {itemNamePresent && (
                <FieldWarning text="Please enter item name" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Item Local Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter local name"
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Item Code
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder="Enter item code"
                value={itemCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setItemCode(val);
                }}
              />
              {itemCodePresent && (
                <FieldWarning text="Please enter item code" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Unique Item Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter unique item code"
                value={uniqueItemCode}
                onChange={(e) => setUniqueItemCode(e.target.value)}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Item Category
                <Req />
              </Form.Label>
              <Form.Select
                value={isCustomCategory ? "Add New Category" : itemCategory}
                onChange={(e) => {
                  const selected = e.target.value;
                  if (selected === "Add New Category") {
                    setIsCustomCategory(true);
                  } else {
                    setIsCustomCategory(false);
                  }
                  setItemCategory(selected);
                }}
              >
                <option value="">Select Category</option>
                {categoryOptions.map((category, idx) => (
                  <option key={idx} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
              {itemCategoryPresent && !isCustomCategory && (
                <FieldWarning text="Please select a valid category" />
              )}
            </Col>
            {isCustomCategory && (
              <Col xs={12} md={6}>
                <Form.Label style={labelStyle}>
                  New Category
                  <Req />
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter New Category"
                  value={newCategory}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^A-Za-z ]/g, "");
                    setNewCategory(val);
                  }}
                />
                {newCategoryPresent && (
                  <FieldWarning text="Please enter New Category" />
                )}
              </Col>
            )}
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Veg ?
                <Req />
              </Form.Label>
              <YesNoRadios
                name="isVegRadio"
                value={isVeg}
                onChange={setIsVeg}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Currently Selling ?
                <Req />
              </Form.Label>
              <YesNoRadios
                name="currentlySellingRadio"
                value={currentlySelling}
                onChange={setCurrentlySelling}
              />
              {currentlySellingPresent && (
                <FieldWarning text="Please select one option" />
              )}
            </Col>
          </Row>
        </SectionCard>

        {/* Pricing */}
        <SectionCard
          icon={<FaRupeeSign size={15} />}
          title="Pricing & Sizes"
          subtitle="Price, discounts and size options"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Available in different sizes ?
                <Req />
              </Form.Label>
              <YesNoRadios
                name="setIsAvailableInMoreSizes"
                value={isAvailableInMoreSizes}
                onChange={setIsAvailableInMoreSizes}
              />
            </Col>

            {isAvailableInMoreSizes ? (
              <Col xs={12} sm={6}>
                <Form.Label style={labelStyle}>
                  Number of Available Sizes
                  <Req />
                </Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={availableSizes}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setAvailableSizes(val);
                  }}
                />
              </Col>
            ) : (
              <>
                <Col xs={6} sm={6} md={3}>
                  <Form.Label style={labelStyle}>
                    Item Price
                    <Req />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="₹"
                    value={originalPrice}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setOriginalPrice(val);
                    }}
                  />
                  {originalPricePresent && (
                    <FieldWarning text="Please enter price" />
                  )}
                </Col>
                <Col xs={6} sm={6} md={3}>
                  <Form.Label style={labelStyle}>Making Cost</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="₹"
                    value={makingCost}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setMakingCost(val);
                    }}
                  />
                </Col>
                <Col xs={6} sm={6} md={3}>
                  <Form.Label style={labelStyle}>Discount %</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="%"
                    value={discountpercent}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setDiscountPercent(val);
                    }}
                  />
                </Col>
                <Col xs={6} sm={6} md={3}>
                  <Form.Label style={labelStyle}>Discount Amount</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="₹"
                    value={discountAmount}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setDiscountAmount(val);
                    }}
                  />
                </Col>
              </>
            )}

            {isAvailableInMoreSizes && Number(availableSizes) > 0 && (
              <Col xs={12}>
                {[...Array(Number(availableSizes))].map((_, index) => (
                  <div key={index} style={sizeFieldStyle}>
                    <div style={{ ...labelStyle, marginBottom: "0.6rem" }}>
                      Size {index + 1}
                    </div>
                    <Row style={{ rowGap: "0.6rem" }}>
                      <Col xs={6} md={3}>
                        <Form.Control
                          type="text"
                          placeholder="* Size Name"
                          value={sizesData[index]?.sizeName || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "sizeName",
                              value: e.target.value,
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} md={3}>
                        <Form.Control
                          type="text"
                          placeholder="* Price"
                          value={sizesData[index]?.itemPrice || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "itemPrice",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} md={3}>
                        <Form.Control
                          type="text"
                          placeholder="* Making Cost"
                          value={sizesData[index]?.makingCost || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "makingCost",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} md={3}>
                        <Form.Control
                          type="text"
                          placeholder="* Reduce Qty"
                          value={sizesData[index]?.consumptionQty || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "consumptionQty",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} md={3}>
                        <Form.Control
                          type="text"
                          placeholder="Discount %"
                          value={sizesData[index]?.discountPercent || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "discountPercent",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} md={3}>
                        <Form.Control
                          type="text"
                          placeholder="Discount ₹"
                          value={sizesData[index]?.discountAmount || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "discountAmount",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Size Description (eg. 250gm)"
                          value={sizesData[index]?.sizeDescription || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "sizeDescription",
                              value: e.target.value,
                            })
                          }
                        />
                      </Col>
                    </Row>
                  </div>
                ))}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleSubmit}
                >
                  Add Sizes
                </Button>
              </Col>
            )}
          </Row>
        </SectionCard>

        {/* Stock, tax and extras */}
        <SectionCard
          icon={<FaBoxOpen size={15} />}
          title="Stock, Tax & Extras"
          subtitle="Quantity, GST, description and photo"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={6} md={3}>
              <Form.Label style={labelStyle}>
                Item Quantity
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder="Enter quantity"
                value={quantity}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setQuantity(val);
                }}
              />
              {quantityPresent && (
                <FieldWarning text="Please enter the number of items" />
              )}
            </Col>
            <Col xs={6} md={3}>
              <Form.Label style={labelStyle}>
                Stop Online order when available quantity reaches this count
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={onlineAvailableThreshold}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setOnlineAvailableThreshold(val);
                }}
              />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label style={labelStyle}>
                GST Tax %
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder="Enter GST %"
                value={itemGst}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setItemGst(val);
                }}
              />
              {itemGstPresent && (
                <FieldWarning text="Please enter valid GST value" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Item Image</Form.Label>
              <Form.Control
                type="file"
                accept=".jpeg,.jpg,.png,.tiff,.svg,.webp,.avif,.jfif"
                onChange={handleFileChange}
              />
              {file && !fileSizeError && !fileInvalidError && (
                <small style={{ color: "#1e7e34" }}>
                  Image selected and ready to upload
                </small>
              )}
              {fileSizeError && (
                <FieldWarning text="Image size should be less than 5 MB, please select a different image" />
              )}
              {fileInvalidError && (
                <FieldWarning text="Only jpeg, jpg, png, tiff, svg, webp and avif formats are supported" />
              )}
            </Col>
            <Col xs={12}>
              <Form.Label style={labelStyle}>Item Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter item description"
                style={{ resize: "vertical" }}
              />
            </Col>
          </Row>
        </SectionCard>

        {/* Actions */}
        <div
          style={{
            ...cardStyle,
            padding: "0.9rem 1rem",
            marginBottom: "1.5rem",
          }}
        >
          <div
            className="d-flex gap-2"
            style={{ width: "100%", maxWidth: "460px", margin: "0 auto" }}
          >
            <Button
              className={`${userInfo?.themeColor}ThemeHeaderTop`}
              style={{ flex: 1, padding: "0.6rem", fontWeight: "600" }}
              onClick={submitHandler}
              disabled={isLoading || isPhotoUploading}
            >
              {isLoading || isPhotoUploading ? "Submitting..." : "Submit"}
            </Button>
            <Button
              variant="secondary"
              style={{ flex: 1, padding: "0.6rem" }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default CreateFoodItems;
